import { Box, Drawer, Typography, IconButton, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Switch, Select, MenuItem, Slider, Grid, TextField, Button, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ColorPicker from '../../../components/ColorPicker';
import { commonSelectStyles, commonMenuProps, commonInputStyles, commonSwitchStyles } from '../../../theme/CommonStyles';
import { menuItemSX } from '../../../components/Builder/BuilderConst';
import React, { useEffect, useRef, useState } from 'react';

const ALLOWED_VARS = ['firstName', 'lastName', 'email', 'phoneNumber', 'website', 'businessName', 'agencyName', 'report_link'];

interface AgencyWidgetDrawerProps {
    isDrawerOpen: boolean;
    setIsDrawerOpen: (val: boolean) => void;
    widgetConfig: any;
    setWidgetConfig: (val: any) => void;
    handleSaveWidgetConfig: () => void;
    config: any;
}

const AgencyWidgetDrawer: React.FC<AgencyWidgetDrawerProps> = ({
    isDrawerOpen,
    setIsDrawerOpen,
    widgetConfig,
    setWidgetConfig,
    handleSaveWidgetConfig,
    config
}) => {
    const [drawerTabValue, setDrawerTabValue] = useState(0);
    const [generatedGridOptions, setGeneratedGridOptions] = useState<{ key: number; label: string }[]>([]);
    const [selectGrid, setSelectGrid] = useState('');
    const emailTemplateRef = useRef<HTMLTextAreaElement>(null);

    const [apiKeyError, setApiKeyError] = useState<string | null>(null);
    const [locationIdError, setLocationIdError] = useState<string | null>(null);

    useEffect(() => {
        if (config) {
            const gridArray: any = config.grid_array || [];
            const costPerPin = Number(config?.per_pin_cost || 0);

            const options = gridArray.map((item: any) => {
                const key = Number(Object.keys(item)[0]);
                const value = key * key * costPerPin;

                return {
                    key,
                    label: `${key}x${key} grid (${value} Credits)`
                };
            });
            setGeneratedGridOptions(options);

            // Sync selectGrid with current widgetConfig
            const matched = options.find((opt: any) => opt.key === widgetConfig.grid_multiplier);
            if (matched) {
                setSelectGrid(matched.label);
            }
        }
    }, [config, widgetConfig.grid_multiplier]);

    const handleGridChange = (event: any) => {
        const label = event.target.value;
        setSelectGrid(label);
        const key = parseInt(label.match(/\d+/)?.[0] || '0', 10);
        setWidgetConfig({
            ...widgetConfig,
            grid_multiplier: key
        });
    };

    const handleSave = () => {
        let isValid = true;

        if (widgetConfig.crm_enable) {
            const isNull = (value: undefined | null | string) => {
                return value === undefined || value === null || value?.trim() === "";
            };

            if (isNull(widgetConfig.go_high_level_api_key)) {
                isValid = false;
                setApiKeyError("GoHighLevel API key is required");
            } else {
                setApiKeyError(null);
            }

            if (isNull(widgetConfig.go_high_level_location_id)) {
                isValid = false;
                setLocationIdError("GoHighLevel Location ID is required ");
            } else {
                setLocationIdError(null);
            }
        } else {
            setApiKeyError(null);
            setLocationIdError(null);
        }

        if (isValid) {
            handleSaveWidgetConfig();
        } else {
            setDrawerTabValue(2); // Auto-switch to CRM tab if there is an error
        }
    };

    const handleInsertVariable = (variable: string) => {
        const input = emailTemplateRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const text = widgetConfig.email_template || '';
        const variableTag = `{{${variable}}}`;

        const newText = text.substring(0, start) + variableTag + text.substring(end);

        setWidgetConfig({
            ...widgetConfig,
            email_template: newText
        });

        // Re-focus and set cursor position after the inserted tag
        setTimeout(() => {
            input.focus();
            input.setSelectionRange(start + variableTag.length, start + variableTag.length);
        }, 0);
    };
    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        >
            <Box sx={{
                width: { xs: 300, md: 400 },
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden" // Prevent double scrollbars
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-family-rubik)' }}>
                        Edit Widget
                    </Typography>
                    <IconButton onClick={() => setIsDrawerOpen(false)} size="small" sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Tabs value={drawerTabValue} onChange={(e, val) => setDrawerTabValue(val)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tab label="Builder" sx={{ textTransform: 'capitalize', fontWeight: 600, fontFamily: 'var(--font-family-manrope)' }} />
                    <Tab label="Settings" sx={{ textTransform: 'capitalize', fontWeight: 600, fontFamily: 'var(--font-family-manrope)' }} />
                    <Tab label="CRM" sx={{ textTransform: 'capitalize', fontWeight: 600, fontFamily: 'var(--font-family-manrope)' }} />
                </Tabs>
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, mr: -1 }}>
                    {drawerTabValue === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Fields appearance */}
                            <Accordion
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: '40px !important',
                                        padding: '0px',
                                        '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, fontFamily: 'var(--font-family-manrope) !important' }}>
                                        Fields
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '0', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {['firstName', 'lastName', 'phoneNumber', /*'email',*/ 'website', /*'companyName',*/ /* 'businessName' */].map((fieldKey) => {
                                        const config: any = widgetConfig?.contact_fields?.[fieldKey as keyof typeof widgetConfig.contact_fields] || { enabled: true, required: 'Optional' };
                                        const labels: any = {
                                            firstName: "First Name",
                                            lastName: "Last Name",
                                            phoneNumber: "Phone Number",
                                            // email: "Email",
                                            website: "Website",
                                            // companyName: "Company Name",
                                            // businessName: "Business Name"
                                        };
                                        return (
                                            <Box key={fieldKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Switch
                                                        checked={fieldKey === 'email' ? true : config.enabled}
                                                        onChange={(e) => {
                                                            if (fieldKey === 'email') return;
                                                            setWidgetConfig({
                                                                ...widgetConfig,
                                                                contact_fields: {
                                                                    ...widgetConfig.contact_fields,
                                                                    [fieldKey]: { ...config, enabled: e.target.checked }
                                                                } as any
                                                            })
                                                        }}
                                                        sx={commonSwitchStyles}
                                                    />
                                                    <Typography sx={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-family-manrope)' }}>
                                                        {labels[fieldKey]}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ width: 110 }}>
                                                    <Select
                                                        size="small"
                                                        value={fieldKey === 'email' ? 'Required' : config.required}
                                                        disabled={fieldKey !== 'email' && !config.enabled}
                                                        onChange={(e) => {
                                                            if (fieldKey === 'email') return;
                                                            setWidgetConfig({
                                                                ...widgetConfig,
                                                                contact_fields: {
                                                                    ...widgetConfig.contact_fields,
                                                                    [fieldKey]: { ...config, required: e.target.value }
                                                                } as any
                                                            })
                                                        }}
                                                        MenuProps={commonMenuProps}
                                                        displayEmpty
                                                        sx={{
                                                            ...commonSelectStyles,
                                                            '& .MuiSelect-select': {
                                                                fontSize: 14,
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="Required" sx={menuItemSX}>Required</MenuItem>
                                                        <MenuItem value="Optional" sx={menuItemSX}>Optional</MenuItem>
                                                    </Select>
                                                </Box>
                                            </Box>
                                        )
                                    })}
                                </AccordionDetails>
                            </Accordion>

                            {/* Default Scan Setting */}
                            <Accordion
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: '40px !important',
                                        padding: '0px',
                                        '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, fontFamily: 'var(--font-family-manrope) !important' }}>
                                        Default Scan Setting
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>Select Grid</Typography>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    value={selectGrid}
                                                    onChange={handleGridChange}
                                                    displayEmpty
                                                    MenuProps={commonMenuProps}
                                                    sx={{
                                                        ...commonSelectStyles,
                                                        width: '100%',
                                                        '& .MuiSelect-select': {
                                                            ...commonSelectStyles['& .MuiSelect-select'],
                                                            fontSize: 'var(--small-body-font-size)',
                                                            fontWeight: 400,
                                                            fontFamily: 'var(--font-family-manrope)'
                                                        }
                                                    }}
                                                    renderValue={(selected) => (
                                                        <Typography sx={{ fontSize: 'var(--small-body-font-size)', fontWeight: 400, fontFamily: 'var(--font-family-manrope)' }}>
                                                            {selected || 'Select Grid'}
                                                        </Typography>
                                                    )}
                                                >
                                                    {generatedGridOptions.map(opt => (
                                                        <MenuItem key={opt.key} value={opt.label} sx={menuItemSX}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>Radius</Typography>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={widgetConfig.radius}
                                                    onChange={(e) => setWidgetConfig({ ...widgetConfig, radius: Number(e.target.value) })}
                                                    sx={commonInputStyles}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Widget appearance */}
                            <Accordion
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: '40px !important',
                                        padding: '0px',
                                        '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, fontFamily: 'var(--font-family-manrope) !important' }}>
                                        Widget appearance
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Widget Background Color"
                                                value={widgetConfig.widget_background_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, widget_background_color: val })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Text Input Border Color"
                                                value={widgetConfig.text_input_border_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, text_input_border_color: val })}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Title Text Color"
                                                value={widgetConfig.title_text_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, title_text_color: val })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Back Button Action Color"
                                                value={widgetConfig.back_button_action_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, back_button_action_color: val })}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Text Input Background"
                                                value={widgetConfig.text_input_background_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, text_input_background_color: val })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Text Input Text Color"
                                                value={widgetConfig.text_input_text_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, text_input_text_color: val })}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>Body font</Typography>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    value={widgetConfig.font_family}
                                                    onChange={(e) => setWidgetConfig({ ...widgetConfig, font_family: e.target.value })}
                                                    displayEmpty
                                                    MenuProps={commonMenuProps}
                                                    sx={{
                                                        ...commonSelectStyles,
                                                        width: '100%',
                                                        '& .MuiSelect-select': {
                                                            ...commonSelectStyles['& .MuiSelect-select'],
                                                            fontSize: 'var(--small-body-font-size)',
                                                            fontWeight: 400,
                                                            fontFamily: 'var(--font-family-manrope)'
                                                        }
                                                    }}
                                                    renderValue={(selected) => (
                                                        <Typography sx={{ fontSize: 'var(--small-body-font-size)', fontWeight: 400, fontFamily: 'var(--font-family-manrope)' }}>
                                                            {selected === 'inherit' ? 'Inherit (Default)' : (selected || 'Select Font')}
                                                        </Typography>
                                                    )}
                                                >
                                                    {['inherit', 'Inter', 'Arial', 'Times New Roman', 'Helvetica'].map(font => (
                                                        <MenuItem key={font} value={font} sx={{ ...menuItemSX, fontFamily: font }}>{font === 'inherit' ? 'Inherit (Default)' : font}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Box sx={{ pr: 2 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>Font Size ({widgetConfig.font_size})</Typography>
                                                <Slider
                                                    value={widgetConfig.font_size}
                                                    onChange={(e, val) => setWidgetConfig({ ...widgetConfig, font_size: val as number })}
                                                    min={10}
                                                    max={20}
                                                    sx={{
                                                        color: '#0D6EFD',
                                                        height: 6,
                                                        '& .MuiSlider-track': { border: 'none' },
                                                        '& .MuiSlider-thumb': {
                                                            width: 18,
                                                            height: 18,
                                                            backgroundColor: '#0D6EFD',
                                                            border: '2px solid white',
                                                            '&:hover': { boxShadow: '0px 0px 0px 6px rgba(13,110,253,0.16)' }
                                                        },
                                                        '& .MuiSlider-rail': { opacity: 1, backgroundColor: '#e0e0e0' },
                                                        boxShadow: 'none !important',
                                                        '&:hover': { boxShadow: 'none' },
                                                        '&:focus': { boxShadow: 'none' },
                                                        '&:active': { boxShadow: 'none' }
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 12 }}>
                                            <Box sx={{ pr: 2 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>Widget Width ({widgetConfig.widget_width}%)</Typography>
                                                <Slider
                                                    value={widgetConfig.widget_width}
                                                    onChange={(e, val) => setWidgetConfig({ ...widgetConfig, widget_width: val as number })}
                                                    min={50}
                                                    max={100}
                                                    sx={{
                                                        color: '#0D6EFD',
                                                        height: 6,
                                                        '& .MuiSlider-track': { border: 'none' },
                                                        '& .MuiSlider-thumb': {
                                                            width: 18,
                                                            height: 18,
                                                            backgroundColor: '#0D6EFD',
                                                            border: '2px solid white',
                                                            '&:hover': { boxShadow: '0px 0px 0px 6px rgba(13,110,253,0.16)' }
                                                        },
                                                        '& .MuiSlider-rail': { opacity: 1, backgroundColor: '#e0e0e0' },
                                                        boxShadow: 'none !important',
                                                        '&:hover': { boxShadow: 'none' },
                                                        '&:focus': { boxShadow: 'none' },
                                                        '&:active': { boxShadow: 'none' }
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5, fontFamily: 'var(--font-family-manrope)', fontStyle: 'italic' }}>
                                                    Note: The width is applied based on the full width of the website where the script is run.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Button appearance */}
                            <Accordion
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: '40px !important',
                                        padding: '0px',
                                        '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, fontFamily: 'var(--font-family-manrope) !important' }}>
                                        Button appearance
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px' }}>Continue Button Text</Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={widgetConfig.continue_button_text}
                                            onChange={(e) => setWidgetConfig({ ...widgetConfig, continue_button_text: e.target.value })}
                                            sx={commonInputStyles}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px' }}>Submit Button Text</Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={widgetConfig.button_text}
                                            onChange={(e) => setWidgetConfig({ ...widgetConfig, button_text: e.target.value })}
                                            sx={commonInputStyles}
                                        />
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Continue Button Color"
                                                value={widgetConfig.continue_button_background_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, continue_button_background_color: val })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Continue Button Text Color"
                                                value={widgetConfig.continue_button_text_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, continue_button_text_color: val })}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Submit Button Color"
                                                value={widgetConfig.submit_button_background_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, submit_button_background_color: val })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <ColorPicker
                                                label="Submit Button Text Color"
                                                value={widgetConfig.submit_button_text_color}
                                                onChange={(val) => setWidgetConfig({ ...widgetConfig, submit_button_text_color: val })}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {/* Success Message */}
                            <Accordion
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    // borderBottom: '1px solid #e0e0e0',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: '40px !important',
                                        padding: '0px',
                                        '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 400, fontFamily: 'var(--font-family-manrope) !important' }}>
                                        Success Message
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>
                                            Success scan message
                                        </Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            value={widgetConfig.success_message}
                                            onChange={(e) => setWidgetConfig({ ...widgetConfig, success_message: e.target.value })}
                                            sx={{
                                                ...commonInputStyles,
                                                '& .MuiInputBase-root': {
                                                    height: 'auto !important',
                                                    minHeight: '100px',
                                                    alignItems: 'start',
                                                    padding: '10px 12px',
                                                    fontFamily: 'var(--font-family-manrope)',
                                                    fontSize: '14px',
                                                    '& textarea': {
                                                        lineHeight: '1.5'
                                                    }
                                                }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 1, fontFamily: 'var(--font-family-manrope)', fontStyle: 'italic', lineHeight: '1.4' }}>
                                            Note: This message will be displayed to your customers immediately after they complete a successful scan on your website.
                                        </Typography>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}

                    {drawerTabValue === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                            {/* Internal Notification */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    mb: 2.5
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontFamily: 'var(--font-family-manrope)',
                                            fontWeight: 400
                                        }}
                                    >
                                        Internal Notification
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                                        Receive an email when a new lead is generated.
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={widgetConfig.notify_on_new_report}
                                    onChange={(e) => setWidgetConfig({ ...widgetConfig, notify_on_new_report: e.target.checked })}
                                    sx={commonSwitchStyles}
                                />
                            </Box>

                            {/* Email Report */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    mb: 2.5
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontFamily: 'var(--font-family-manrope)',
                                            fontWeight: 400
                                        }}
                                    >
                                        Email Report
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                                        Automatically send this ranking report via email.
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={widgetConfig.notify_user_with_report}
                                    onChange={(e) => setWidgetConfig({ ...widgetConfig, notify_user_with_report: e.target.checked })}
                                    sx={commonSwitchStyles}
                                />
                            </Box>

                            {widgetConfig.notify_user_with_report && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 400, fontFamily: 'var(--font-family-manrope)' }}>
                                            Email Builder
                                        </Typography>
                                        <Tooltip
                                            title="Add Custom Variable"
                                            placement="top"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#ffffff',
                                                        color: '#5e5d5dff',
                                                        border: '1px solid #d8d8d8',
                                                        fontSize: '12px',
                                                        borderRadius: 'var(--small-border-radius)',
                                                        fontWeight: 500,
                                                        p: 1
                                                    }
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: '#d8d8d8'
                                                    }
                                                }
                                            }}
                                            arrow>
                                            <Box>
                                                <Select
                                                    size="small"
                                                    value=""
                                                    displayEmpty
                                                    IconComponent={() => null}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleInsertVariable(e.target.value);
                                                        }
                                                    }}
                                                    renderValue={() => (
                                                        <AddCircleOutlineIcon sx={{ fontSize: '20px', display: 'block' }} />
                                                    )}
                                                    MenuProps={commonMenuProps}
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                        minWidth: '32px',
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: '#eff6ff',
                                                        borderRadius: '6px',
                                                        color: '#0D6EFD',
                                                        '& .MuiSelect-select': {
                                                            p: '0 !important',
                                                            pr: '0 !important',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '32px',
                                                            height: '32px'
                                                        },
                                                        '&:hover': { backgroundColor: '#dbeafe' }
                                                    }}
                                                >
                                                    {ALLOWED_VARS.map((variable) => (
                                                        <MenuItem
                                                            key={variable}
                                                            value={variable}
                                                            sx={{
                                                                ...menuItemSX,
                                                            }}
                                                        >
                                                            {`{${variable}}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        </Tooltip>
                                    </Box>

                                    <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1, fontFamily: 'var(--font-family-manrope)', fontStyle: 'italic', lineHeight: '1.4' }}>
                                        Tip: Use the <b>+</b> icon to insert custom variables like {'{{firstName}}'} into your email template.
                                    </Typography>

                                    <TextField
                                        multiline
                                        rows={6}
                                        fullWidth
                                        inputRef={emailTemplateRef}
                                        value={widgetConfig.email_template}
                                        onChange={(e) => setWidgetConfig({ ...widgetConfig, email_template: e.target.value })}
                                        placeholder="Enter your email template here..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                fontFamily: 'var(--font-family-manrope)',
                                                fontSize: '14px',
                                                backgroundColor: '#fafafa'
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    )}

                    {drawerTabValue === 2 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                            {/* CRM Integration Toggle */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    // mb: 2.5,
                                    mb: 0.5
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontFamily: 'var(--font-family-manrope)',
                                            fontWeight: 400
                                        }}
                                    >
                                        HighLevel Integration
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={widgetConfig.crm_enable}
                                    onChange={(e) => {
                                        setWidgetConfig({ ...widgetConfig, crm_enable: e.target.checked });
                                        if (!e.target.checked) {
                                            setApiKeyError(null);
                                            setLocationIdError(null);
                                        }
                                    }}
                                    sx={commonSwitchStyles}
                                />
                            </Box>

                            {/* GoHighLevel Fields — shown only when CRM is enabled */}
                            {widgetConfig.crm_enable && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>
                                            GoHighLevel API Key
                                        </Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Enter your GHL API key"
                                            value={widgetConfig.go_high_level_api_key || ''}
                                            onChange={(e) => {
                                                setWidgetConfig({ ...widgetConfig, go_high_level_api_key: e.target.value });
                                                setApiKeyError(null);
                                            }}
                                            error={!!apiKeyError}
                                            helperText={apiKeyError}
                                            sx={commonInputStyles}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 400, marginBottom: '6px', fontFamily: 'var(--font-family-manrope)' }}>
                                            GoHighLevel Location ID
                                        </Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Enter your GHL location ID"
                                            value={widgetConfig.go_high_level_location_id || ''}
                                            onChange={(e) => {
                                                setWidgetConfig({ ...widgetConfig, go_high_level_location_id: e.target.value });
                                                setLocationIdError(null);
                                            }}
                                            error={!!locationIdError}
                                            helperText={locationIdError}
                                            sx={commonInputStyles}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{
                    position: 'sticky',
                    bottom: -24,
                    backgroundColor: '#fff',
                    pt: 2,
                    // pb: 2,
                    mt: 'auto',
                    borderTop: '1px solid #e0e0e0',
                    zIndex: 10,
                    ml: '-24px',
                    mr: '-24px',
                    px: 3,
                    display: "flex",
                    justifyContent: "flex-end"
                }}>
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        onClick={handleSave}
                        sx={{
                            textTransform: 'none',
                            fontSize: '16px',
                            fontFamily: 'var(--font-family-manrope)',
                            fontWeight: 600,
                            padding: 'var(--action-button-small-padding)',
                            backgroundColor: 'var(--action-button-background-color)',
                            color: 'white',
                            borderRadius: 'var(--action-button-radius)',
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AgencyWidgetDrawer;
